import React, { useState } from "react";
import classes from "./transaction-history-container.module.css";
import cx from "classnames";
import {
  PENDING,
  transactionKeyLabelMapDesktop,
  transactionLabelMap
} from "../../services/constants";
import TransactionTableStatus from "./TransactionTableStatus";
import EmptyState from "./EmptyState";
import { JhUseToast } from "designPackages/toastPackage";
import useModalStore from "zustandStores/useModalStore";
import { JhText } from "designPackages/textPackage";
import { getInvoiceEmail } from "utils/invoiceUtils";

const TableHeaders = props => {
  const { headerData } = props;
  return headerData.map((header, index) => (
    <th key={index}>
      <div className={cx(header.class, classes.tableHeader)}>
        {header.label}
      </div>
    </th>
  ));
};

const TransactionRows = props => {
  const { transactionData = [], onInvoiceClick, loadingInvoiceId } = props;
  return transactionData.map((row, index) => {
    const {
      transactionLabel = "",
      lastUpdated = "",
      status = "",
      credits = "",
      expirationDate = "",
      transactionDate = "",
      transactionTime = "",
      closingBalance = "",
      activity = "",
      isInvoiceAvailable = false,
      invoiceEmail = "",
      orderId = ""
    } = row;

    const labelValues = transactionLabelMap[transactionLabel] || [];

    const transactionSubLabel = Object.fromEntries(
      Object.entries(row).filter(
        ([key, value]) => labelValues.includes(key) && value != null
      )
    );

    return (
      <tr key={index}>
        <td>
          <div className={cx(classes.transactionHeader, classes.tableData)}>
            <div className={cx(classes.transactionLabel, classes.cellHeading)}>
              {transactionLabel}
            </div>

            <div
              className={cx(
                classes.transactionSubLabel,
                classes.cellSubHeading
              )}
            >
              {Object.entries(transactionSubLabel).map(([key, value]) => (
                <div key={key}>
                  <span>
                    {key === "jobId" && activity === "JOB_BOOST"
                      ? transactionKeyLabelMapDesktop["boostedJobId"]
                      : transactionKeyLabelMapDesktop[key]}
                  </span>
                  <span>{value}</span>
                </div>
              ))}
            </div>
          </div>
        </td>
        <td>
          <div className={cx(classes.cellHeader, classes.tableData)}>
            <div className={classes.cellHeading}>{credits}</div>
            <div className={classes.cellSubHeading}>{expirationDate}</div>
          </div>
        </td>
        <td>
          <div className={cx(classes.cellHeader, classes.tableData)}>
            <TransactionTableStatus status={status} />
            <div className={classes.cellSubHeading}>{lastUpdated}</div>
          </div>
        </td>
        <td>
          <div className={cx(classes.cellHeader, classes.tableData)}>
            <div className={classes.cellHeading}>{transactionDate}</div>
            <div className={classes.cellSubHeading}>{transactionTime}</div>
          </div>
        </td>
        <td>
          <div
            className={cx(
              classes.balanceHeader,
              classes.tableData,
              classes.balanceData
            )}
          >
            {status === PENDING ? "--" : closingBalance}
          </div>
        </td>
        <td>
          <div className={cx(classes.cellHeader, classes.tableData)}>
            {isInvoiceAvailable && orderId ? (
              <JhText
                variant="caption1"
                color="primary"
                className="cursor-pointer"
                onClick={() => onInvoiceClick({ orderId, invoiceEmail })}
                disabled={loadingInvoiceId === orderId}
              >
                Get Invoice
              </JhText>
            ) : (
              <div className={classes.cellHeading}>--</div>
            )}
          </div>
        </td>
      </tr>
    );
  });
};

const ReservedRows = props => {
  const { transactionData = [] } = props;
  return transactionData.map((row, index) => {
    const {
      jobTitle = "",
      cityLocalityData = "",
      salary = "",
      openings = "",
      jobId = "",
      credits = "",
      status = "",
      jobPostingDate = "",
      jobPostingTime = "",
      companyName = ""
    } = row;

    return (
      <tr key={index}>
        <td>
          <div
            className={cx(
              classes.jobDetailsHeader,
              classes.tableData,
              classes.reservedHeader
            )}
          >
            <div className={cx(classes.transactionLabel, classes.cellHeading)}>
              {jobTitle}
            </div>
            <div
              className={cx(
                classes.transactionSubLabel,
                classes.cellSubHeading
              )}
            >
              {companyName ? (
                <div className={classes.companyName}>
                  Posted for {companyName}
                </div>
              ) : null}
              <div>{cityLocalityData}</div>
              <div>
                {salary ? <>{salary} | </> : null}
                {openings}
              </div>
              <div>Job Id - {jobId}</div>
            </div>
          </div>
        </td>
        <td>
          <div
            className={cx(
              classes.cellHeader,
              classes.tableData,
              classes.reservedHeader
            )}
          >
            <div className={classes.cellHeading}>{credits}</div>
          </div>
        </td>
        <td>
          <div
            className={cx(
              classes.cellHeader,
              classes.tableData,
              classes.reservedHeader
            )}
          >
            <TransactionTableStatus status={status} />
          </div>
        </td>
        <td>
          <div
            className={cx(
              classes.cellHeader,
              classes.tableData,
              classes.reservedHeader
            )}
          >
            <div className={classes.cellHeading}>{jobPostingDate}</div>
            <div className={classes.cellSubHeading}>{jobPostingTime}</div>
          </div>
        </td>
      </tr>
    );
  });
};

function TransactionTable(props) {
  const {
    transactionData = [],
    headerData,
    isReservedTab,
    showFilters
  } = props;
  const { toast } = JhUseToast();

  const { setModalData } = useModalStore(state => ({
    modalData: state.modalData,
    setModalData: state.setModalData
  }));

  const [loadingInvoiceId, setLoadingInvoiceId] = useState(null);

  const handleInvoiceClick = async ({ orderId, invoiceEmail }) => {
    await getInvoiceEmail({
      orderId,
      invoiceEmail,
      toast,
      setLoadingInvoiceId,
      setModalData
    });
  };

  return (
    <>
      <div
        className={cx(
          isReservedTab || !showFilters ? classes.tableTopBorder : null,
          classes.tableContainer
        )}
      >
        {!transactionData.length ? (
          <EmptyState />
        ) : (
          <table className={classes.table} cellPadding={0}>
            <tr>
              <TableHeaders headerData={headerData} />
            </tr>
            {isReservedTab ? (
              <ReservedRows transactionData={transactionData} />
            ) : (
              <TransactionRows
                transactionData={transactionData}
                onInvoiceClick={({ orderId, invoiceEmail }) =>
                  handleInvoiceClick({ orderId, invoiceEmail })
                }
                loadingInvoiceId={loadingInvoiceId}
              />
            )}
          </table>
        )}
      </div>
    </>
  );
}

export default TransactionTable;
